import useUrlState from '@ahooksjs/use-url-state';
import { ProFormInstance } from '@ant-design/pro-form';
import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import { logger } from '@/infrastructure/logger';
import { createReactionsMap, applyReaction } from '@/infrastructure/schema/reaction-engine';
import { useTdsForm } from '@/presentation/hooks/form';
import { StandardCommonFieldKey, Zone } from '@/proto/field';
import { IFilter } from '@/proto/metric';
import { genStandardToken, parseStandardToken } from '@/proto/token';
import { appManagerService, MonitorKey } from '@/service/app-manager';
import { TdsForm, TdsFormProps, ITdsFormRef } from '../form';
import { mockSimpleSchema } from './mock-schema';
import { TdsFormItemSchema, SchemaConverterOptions } from './schema';
import { schemaToFormItem } from './schema-converter';

type IFormInstance =
  | React.MutableRefObject<ProFormInstance<any> | undefined>
  | React.RefObject<ProFormInstance<any> | undefined>;

export interface TdsSchemaFormProps extends Omit<TdsFormProps, 'items'> {
  /**
   * Schema 配置数组，支持 simple 和 full 两种模式
   * 如果传了 monitorKey，则 schema 从接口获取，此 prop 可选
   * 如果没传 monitorKey，则必须传此 prop
   */
  schema?: TdsFormItemSchema[];

  /**
   * Zone 上下文，用于动态注入 fieldScope
   */
  zone?: Zone;

  /**
   * 应用配置，用于评估 hidden 等动态条件
   */
  config?: any;

  /**
   * ProForm 的 ref，用于获取表单实例
   * 如果外部没传，则内部自动创建
   */
  formRef?: IFormInstance;

  /**
   * MonitorKey，如果传了此 prop，则从接口获取 schema
   * 如果传了 monitorKey，schema prop 可选
   */
  monitorKey?: MonitorKey;
}

/**
 * TdsSchemaForm 组件
 * 基于 Schema 协议动态渲染表单，支持 simple 和 full 两种模式
 *
 * @example
 * ```tsx
 * <TdsSchemaForm
 *   schema={[
 *     { schemaType: 'simple', name: StandardCommonFieldKey.AbTestTag },
 *     { schemaType: 'simple', name: StandardCommonFieldKey.AccountId },
 *   ]}
 *   zone={Zone.HighCpuStack}
 *   config={appConfig}
 *   onFinish={(values, filters) => {}}
 * />
 * ```
 */
export const TdsSchemaForm: React.FC<TdsSchemaFormProps> = (props) => {
  const { t } = useTranslation();
  const {
    schema: externalSchema,
    zone,
    config,
    onFieldsChange,
    onFinish,
    formRef: externalFormRef,
    monitorKey,
    ...restProps
  } = props;

  // 如果外部没传 formRef，则内部自动创建
  const internalFormRef = useRef<ProFormInstance<any>>(null);
  const formRef = externalFormRef || internalFormRef;

  // TdsForm 的 ref，用于调用表单方法（仅内部使用）
  const tdsFormRef = useRef<ITdsFormRef>(null);

  const prevValuesRef = useRef<Record<string, any>>({});
  const reactionsMapRef = useRef<ReturnType<typeof createReactionsMap>>(new Map());
  const [, useTdsFormContext] = useTdsForm;
  const { updateFormContext } = useTdsFormContext();
  const [state, setState] = useUrlState({
    token: '',
  });

  // 维护动态 schema 状态，用于联动时更新字段配置
  const [dynamicSchema, setDynamicSchema] = useState<TdsFormItemSchema[]>(externalSchema || mockSimpleSchema);

  // 如果传了 monitorKey，从接口获取 schema
  useEffect(() => {
    if (monitorKey) {
      appManagerService
        .getFormListConfig({ monitorKey: monitorKey as string } as any)
        .then((res) => {
          try {
            // formList 是字符串 JSON，需要解析
            const formListStr = res?.formList;
            if (formListStr) {
              const parsedSchema = JSON.parse(formListStr) as TdsFormItemSchema[];
              setDynamicSchema(parsedSchema);
            } else {
              // 如果接口没有返回 formList，使用默认 schema
              setDynamicSchema([
                {
                  schemaType: 'simple',
                  name: StandardCommonFieldKey.TimeRange,
                },
              ]);
            }
          } catch (e) {
            logger.error('Failed to parse formList schema', e);
            // 解析失败时使用默认 schema
            setDynamicSchema(externalSchema || mockSimpleSchema);
          }
        })
        .catch((e) => {
          logger.error('Failed to get form list config', e);
          // 接口调用失败时使用默认 schema
          setDynamicSchema(externalSchema || mockSimpleSchema);
        });
    } else if (externalSchema) {
      // 没有传 monitorKey，使用外部传入的 schema
      setDynamicSchema(externalSchema);
    }
  }, [monitorKey, externalSchema]);

  // 默认的 onFinish 函数：输出表单值和 filters 的日志，方便调试
  const defaultOnFinish = useCallback((values: Record<string, any>, filters: IFilter[]) => {
    console.log('[TdsSchemaForm] Form submitted:', {
      values,
      filters,
    });

    genStandardToken({
      ...values,
      time_window: values.time_window_type,
      filters,
      uuid: uuid.v4(),
    }).then((token) => {
      updateFormContext(draft => ({
        ...draft,
        token,
        fieldsValue: values,
        fieldsFilter: filters,
      }));
      setState({
        token,
      });
    });
  }, []);

  // 使用用户提供的 onFinish，如果没有提供则使用默认的
  const handleFinish = onFinish ?? defaultOnFinish;

  // 将 Schema 转换为 FormItems（使用动态 schema）
  const formItems = useMemo(() => {
    const options: SchemaConverterOptions = { zone, config };
    const items = dynamicSchema.map((s) => {
      try {
        return schemaToFormItem(s, options);
      } catch (error) {
        console.error('Failed to convert schema item:', s, error);
        // 返回一个占位符配置，避免页面崩溃
        return {
          type: 'characterInput' as any,
          name: (s as any).name || 'unknown',
          label: t('配置错误'),
          hidden: true,
        } as any;
      }
    });
    console.log('[TdsSchemaForm] formItems', items);
    return items;
  }, [dynamicSchema, zone, config]);


  // 创建联动配置映射
  useMemo(() => {
    const map = createReactionsMap(formItems);
    reactionsMapRef.current = map;
    return map;
  }, [formItems]);

  // 深度合并对象，支持嵌套属性
  const deepMerge = useCallback((target: any, source: any): any => {
    const result = { ...target };
    Object.keys(source).forEach((key) => {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    });
    return result;
  }, []);

  // 应用联动规则的函数
  const applyReactions = useCallback((currentValues: Record<string, any>) => {
    const formInstance = tdsFormRef.current?.getFormInstance?.();
    if (!formInstance || reactionsMapRef.current.size === 0) {
      return;
    }

    const prevValues = prevValuesRef.current;

    // 遍历所有配置了联动的字段
    const valuesToUpdate: Record<string, any> = {};
    const schemaUpdatesMap = new Map<string, Record<string, any>>();

    reactionsMapRef.current.forEach((reactions, fieldName) => {
      reactions.forEach((reaction) => {
        // 检查依赖是否变化
        const hasChanges = reaction.dependencies.some((depName) => {
          const keys = depName.split('.');
          let prevValue = prevValues;
          let currentValue = currentValues;

          for (const key of keys) {
            prevValue = prevValue?.[key];
            currentValue = currentValue?.[key];
          }

          return JSON.stringify(prevValue) !== JSON.stringify(currentValue);
        });

        if (hasChanges) {
          const result = applyReaction(reaction, formInstance, fieldName, currentValues);

          // 只处理 value 更新（通过 FormInstance）
          if (result.valueUpdate !== undefined) {
            valuesToUpdate[fieldName] = result.valueUpdate;
          }

          // 收集 schema 更新（所有其他更新都在 Schema 层面处理）
          if (result.schemaUpdates) {
            const existingUpdates = schemaUpdatesMap.get(fieldName) || {};
            schemaUpdatesMap.set(fieldName, deepMerge(existingUpdates, result.schemaUpdates));
          }
        }
      });
    });

    // 批量更新字段值（仅 value 更新通过 FormInstance）
    if (Object.keys(valuesToUpdate).length > 0) {
      formInstance.setFieldsValue(valuesToUpdate);
    }

    // 更新 schema（Schema 层面）
    if (schemaUpdatesMap.size > 0) {
      setDynamicSchema(prevSchema => prevSchema.map((s) => {
        const fieldName = String(s.name);
        const updates = schemaUpdatesMap.get(fieldName);
        if (!updates) {
          return s;
        }

        // 深度合并更新到 schema
        if (s.schemaType === 'full') {
          return deepMerge(s, updates);
        }
        // simple schema，更新到 overrides 中
        const mergedOverrides = deepMerge(s.overrides || {}, updates);
        return {
          ...s,
          overrides: mergedOverrides,
        };
      }));
    }

    // 更新缓存的值
    prevValuesRef.current = { ...currentValues };
  }, [deepMerge]);

  // 包装 onFieldsChange，在触发联动之前先更新值
  const handleFieldsChange = useCallback((changedFields: any, allFields: any) => {
    // 获取表单实例和当前值
    const formInstance = tdsFormRef.current?.getFormInstance?.();
    if (formInstance) {
      // 获取当前值
      const currentValues = formInstance.getFieldsValue();

      // 应用联动规则
      applyReactions(currentValues);
    }

    // 调用原始的 onFieldsChange
    onFieldsChange?.(changedFields, allFields);
  }, [applyReactions, onFieldsChange]);

  // 初始化时应用联动规则
  useEffect(() => {
    const formInstance = tdsFormRef.current?.getFormInstance?.();
    if (formInstance && reactionsMapRef.current.size > 0) {
      // 延迟执行，确保表单已完全初始化
      const timer = setTimeout(() => {
        const currentValues = formInstance.getFieldsValue();
        if (Object.keys(currentValues).length > 0) {
          applyReactions(currentValues);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [applyReactions]);

  // 初始化 token：从 URL 中解析 token 并回填表单
  const initToken = useCallback(async () => {
    if (!state.token) {
      return;
    }

    try {
      const fieldsValue = await parseStandardToken(state.token);

      // 链接有token时，强制展示有value值的表单项
      await tdsFormRef.current?.showFormItem?.(formItems, fieldsValue);

      // 设置表单值
      formRef.current?.setFieldsValue?.(fieldsValue);

      // 更新表单上下文
      updateFormContext(draft => ({
        ...draft,
        token: state.token,
        fieldsValue,
        fieldsFilter: fieldsValue?.filters ?? [],
        uuidFetch: uuid.v4(),
      }));
    } catch (error) {
      console.error('[TdsSchemaForm] Failed to init token:', error);
    }
  }, [state.token, formItems, updateFormContext]);

  // 组件挂载时或 token 变化时初始化
  useEffect(() => {
    initToken();
  }, []);

  useEffect(() => {
    if (!state.token) {
      formRef.current?.submit();
    }
  }, []);

  // 使用现有的 TdsForm 组件
  return (
    <div className='bg-white rounded p-4'>
    <TdsForm
      {...restProps}
      items={formItems}
      ref={tdsFormRef}
      formRef={formRef}
      onFieldsChange={handleFieldsChange}
      onFinish={handleFinish}
    />
    </div>
  );
};

TdsSchemaForm.displayName = 'TdsSchemaForm';

